import styled, { keyframes } from 'styled-components';

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/**
 * Chrome only — no positioning. Consumers wrap this in their own
 * absolutely / fixed-positioned container so a `Sheet` can drop into
 * a canvas slot, a side panel, a modal overlay, etc. without baking
 * any of those into the molecule.
 *
 * The slide-up animation matches the canvas RunHistorySheet's enter
 * affordance — short translate + opacity ramp.
 */
export const Root = styled.section`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 0;
  background: ${({ theme: { colors = {} } }) => colors.surface ?? '#121218'};
  animation: ${slideUp} 220ms cubic-bezier(0.2, 0.8, 0.2, 1) both;
`;

/**
 * Header bar — flex row, content on the left, optional trailing
 * actions on the right. Padding matches the editor's RunHistorySheet
 * (14px vertical, 28px horizontal) so the header lands at the same
 * height as the surrounding canvas chrome.
 */
export const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 28px;
  flex: 0 0 auto;
`;

/**
 * Lead slot inside the header — the title (+ optional avatar / icon
 * neighbour) lives here. Separate component so the consumer can drop
 * extra siblings (e.g. a count badge) next to the title without
 * displacing the trailing actions.
 */
export const HeaderLead = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
`;

/**
 * H2 title — 14px / 600 / onSurface. Renders as `<h2>` for screen
 * readers (the sheet's accessible name is taken from the
 * `aria-label` on `<Root>` separately).
 */
export const Title = styled.h2`
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
  color: ${({ theme: { colors = {} } }) => colors.onSurface ?? '#E6E6F2'};
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/**
 * Scrollable body. `flex: 1` so it fills the remaining vertical
 * space; `min-height: 0` is the standard flex-overflow incantation
 * that lets `overflow: auto` actually kick in inside a flex column.
 * Padding matches the header's horizontal gutters.
 */
export const Body = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 0 28px 24px;
`;
